import React, { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { clearTokens } from '../auth/auth';
import { scheduleTokenRefresh } from '../auth/scheduleTokenRefresh';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AUTH_FIELD_BG, AuthPageLayout, authLinkStyle, authPrimaryButtonStyle, authTextFieldSx } from "./auth/AuthPageLayout";
import { api } from "../api/apiService";
import { UserContext, type User } from '../contexts/UserContext';
import { AuthContext } from "../auth/AuthContext";

const LoginComponent = () => {
  const { login } = useContext(AuthContext);
  const { setUser } = useContext(UserContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email: string; password: string }>({ email: '', password: '' });
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const validateEmail = (email: string): boolean => /\S+@\S+\.\S+/.test(email);

  const handleSessionExpired = () => {
    clearTokens(); // Clear tokens when the session expires
    window.location.reload(); // Reload the page to force re-login
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const emailError = validateEmail(email) ? '' : 'Invalid email address';
    setErrors({ email: emailError, password: '' });

    if (emailError) return;

    setIsLoading(true);
    setMessage('');

    try {
      const response = await api.post(
        '/api/auth/login',
        { email, password },
        { skipAuth: true }
      );
      login(response.access_token, response.refresh_token);
      api
        .get('/api/users/me')
        .then((res) => setUser(res as User))
        .catch((err) => {
          console.error('Error fetching user:', err);
        });

      // Schedule token refresh after login
      const tokenExpiryTime = Number(localStorage.getItem('tokenExpiryTime')); // Stored during `storeTokens`
      if (tokenExpiryTime) {
        const expiresIn = tokenExpiryTime - Date.now();
        scheduleTokenRefresh(expiresIn / 1000, handleSessionExpired);
      }

      setMessage('Login successful!');
    } catch (error: any) {
      console.error("Authentication failed:", error);
      setMessage('Authentication failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout title="Login">
      {isLoading ? (
        <CircularProgress sx={{ color: 'white', mt: 2 }} />
      ) : (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <TextField
            autoComplete="username"
            autoFocus
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={authTextFieldSx}
          />
          <TextField
            label="Password"
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            sx={authTextFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{
                      backgroundColor: 'transparent',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: AUTH_FIELD_BG,
                      },
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box textAlign="right" mt={1}>
            <RouterLink to="/forgot-password" style={authLinkStyle}>
              Forgot Password?
            </RouterLink>
          </Box>
          {message && <p className="error-message show">{message}</p>}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            style={authPrimaryButtonStyle}
          >
            Login
          </Button>
        </form>
      )}
    </AuthPageLayout>
  );
};

export default LoginComponent;